import { Stack } from '@chakra-ui/react';
import type { GroupMember } from '@halo/chat-sdk-react';
import {
  GroupStatusEnum,
  useAppStore,
  useGroupMembers,
  type SessionStore,
} from '@halo/chat-sdk-react';
import {
  ChatPanelDrawer,
  GroupInfoCard,
  MembersCard,
  ShareLinkCard,
  tools,
  JoinRequestIcon,
  GroupItem,
  DescriptionCard,
  SaveToContactsCard,
  AutoDeleteMessagesCard,
  ClearAllChatHistory,
  PinnedCard,
  ReportCard,
} from '@halo/ui-chat-react';
import type { RefObject } from 'react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useActionsStore } from '@/store/actions';
import { SyncBackupType, useSettingsStore } from '@/store/settings';
import { FooterButtons } from './FooterButtons';
import { GroupManagement } from './GroupManagement';
import { MuteNotificationsCardPlus } from './MuteNotificationsCardPlus';

export interface ChatContentDrawerProps {
  ref?: RefObject<HTMLDivElement | null>;
  open: boolean;
  sessionStore: SessionStore;
  isGroupManager?: boolean;
  isGroupOwner?: boolean;
  isGroupBanned?: boolean;
  setOpenInfoDrawer: (open: boolean) => void;
}

export const ChatContentDrawer = memo(function ChatContentDrawer({
  open,
  setOpenInfoDrawer,
  sessionStore,
  isGroupManager,
  isGroupOwner,
  isGroupBanned,
}: ChatContentDrawerProps) {
  const { t } = useTranslation();
  const {
    setGroupInviteIsOpen,
    setGroupRemoveMembersIsOpen,
    setQrCodeIsOpen,
    fetchProfileUser,
    setGroupJoinRequestsIsOpen,
    setGroupDescriptionIsOpen,
    setClearChatHistoryIsOpen,
    setReportIsOpen,
    setForbidAddIsOpen,
  } = useActionsStore(
    useShallowEnhance([
      'setGroupInviteIsOpen',
      'setGroupRemoveMembersIsOpen',
      'setQrCodeIsOpen',
      'fetchProfileUser',
      'setGroupJoinRequestsIsOpen',
      'setGroupDescriptionIsOpen',
      'setClearChatHistoryIsOpen',
      'setReportIsOpen',
      'setForbidAddIsOpen',
    ])
  );

  const { client, updateSession } = useAppStore();

  const { session = null, groupInfo = null, setGroupInfo } = sessionStore;
  const { members, run } = useGroupMembers(session?.chat_id);

  const disabledEditor = useMemo(() => {
    return !sessionStore?.groupAccess?.canEditGroupInfo;
  }, [sessionStore?.groupAccess?.canEditGroupInfo]);

  const limitGroupMember = useMemo(() => {
    return groupInfo?.status === GroupStatusEnum.LIMIT;
  }, [groupInfo]);

  const { hideMember } = useMemo(() => {
    return {
      hideMember: !sessionStore?.groupAccess?.canShowMembers,
    };
  }, [sessionStore?.groupAccess?.canShowMembers]);

  const onUpdateSave = useCallback(
    async (name?: string, file?: File | null) => {
      let access_url = session?.avatar || '';
      let group_name = session!.name || '';

      if (file) {
        access_url = (await client?.Api.session.uploadAvatar({ file })) || '';
      }
      if (name) {
        group_name = name;
      }
      await client?.Api.session.setGroupInfo(session?.chat_id || '', group_name, access_url);
      setGroupInfo({
        ...groupInfo!,
        group_name: group_name,
        avatar: access_url,
      });
      updateSession({
        chat_id: session!.chat_id!,
        name: group_name,
        avatar: access_url,
      });
    },
    [client?.Api.session, groupInfo, session, setGroupInfo, updateSession]
  );

  const onRemoveMembers = useCallback(() => {
    setGroupRemoveMembersIsOpen(true, {
      title: t('action.removeMembers'),
      list: [
        {
          label: null,
          items: members || [],
        },
      ],
      session: session!,
    });
  }, [setGroupRemoveMembersIsOpen, members, session, t]);

  const onTransferGroupOwnerClick = useCallback(
    async (member: GroupMember) => {
      await client?.Api.session.transferGroupOwnership(session?.chat_id || '', member.user_id);
      tools.toaster.success({
        title: t('tips.groupOwnershipTransferred'),
      });
    },
    [client?.Api.session, session?.chat_id, t]
  );

  const onMemberAvatarClick = useCallback(
    async (member: any) => {
      fetchProfileUser(member.user_id, true, !sessionStore?.groupAccess?.canPrivateMessage);
    },
    [fetchProfileUser, sessionStore?.groupAccess?.canPrivateMessage]
  );
  const handleJoinRequestsClick = useCallback(() => {
    if (isGroupBanned) {
      tools.toaster.error({
        title: t('tips.groupAbnormal'),
      });
      return;
    }
    setGroupJoinRequestsIsOpen(true);
  }, [setGroupJoinRequestsIsOpen, t, isGroupBanned]);

  // add to contacts
  const syncBackupCloudData = useSettingsStore((state) => state.syncBackupCloudData);
  const {
    data: saveToContactsCategory,
    run: runSaveToContactsCategory,
    loading: loadingSaveToContactsCategory,
  } = useRequest(
    async (gid?: string, op?: 'add' | 'remove') => {
      if (!gid || !client) return;
      if (op) {
        if (op === 'add') {
          await client.Api.contacts.addGroup(gid);
          tools.toaster.success({ title: t('tips.savedToContacts') });
        }
        if (op === 'remove') {
          await client.Api.contacts.removeGroup(gid);
          tools.toaster.success({ title: t('tips.contactsRemoved') });
        }
        syncBackupCloudData({ type: SyncBackupType.CONTACTS }).catch(() => {});
      }
      const res = await client.Api.contacts.queryGroup(gid);
      return res?.category === 1;
    },
    {
      manual: true,
    }
  );
  const group = useMemo(() => sessionStore.groupInfo, [sessionStore.groupInfo]);
  useEffect(() => {
    runSaveToContactsCategory(group?.gid);
  }, [runSaveToContactsCategory, group?.gid]);

  // auto delete messages
  const onDurationChange = useCallback(
    async (duration: number) => {
      const chat_id = sessionStore!.session!.chat_id!;
      await client?.Api.session.setSessionExpireDuration(chat_id, BigInt(duration));
      updateSession({
        chat_id,
        expire_duration: duration,
      });
    },
    [client?.Api.session, sessionStore, updateSession]
  );

  const onClearAllChatHistory_ = useCallback(() => {
    setClearChatHistoryIsOpen(true);
  }, [setClearChatHistoryIsOpen]);

  useEffect(() => {
    if (open) {
      return run();
    }
  }, [open, run]);

  return (
    <ChatPanelDrawer
      title={t('chatSettings.groupSettingsTitle')}
      open={open}
      onOpenChange={setOpenInfoDrawer}
    >
      <Stack gap="2">
        <GroupInfoCard
          closeAllClick={() => setOpenInfoDrawer(false)}
          disabledEditor={disabledEditor}
          avatar={groupInfo?.avatar || ''}
          name={groupInfo?.group_name || ''}
          memberCount={groupInfo?.member_num || 0}
          onUpdateSave={onUpdateSave}
        />
        <ShareLinkCard
          link={groupInfo?.share_url || ''}
          onLinkClick={() =>
            tools.copyToClipboard(
              t('chat.groupShareLink', {
                groupName: groupInfo?.group_name || '',
                shareUrl: groupInfo?.share_url || '',
              })
            )
          }
          onQrCodeClick={() => {
            setQrCodeIsOpen(true, {
              link: groupInfo?.share_url || '',
              avatar: groupInfo?.avatar || '',
              name: groupInfo?.group_name || '',
              username: groupInfo?.group_id || '',
              source: 'group',
              sessionStore: sessionStore,
            });
          }}
          descriptionDom={
            <DescriptionCard
              disabledEditor={disabledEditor}
              description={groupInfo?.description || ''}
              onClick={() => setGroupDescriptionIsOpen(true)}
            />
          }
        />

        <MembersCard
          members={members || []}
          isGroupManager={isGroupManager}
          hideMember={hideMember}
          onAddMembers={() => {
            if (isGroupBanned || limitGroupMember) {
              setForbidAddIsOpen(true);
              return;
            }
            setGroupInviteIsOpen(true, {
              session: session!,
              groupInfo: groupInfo!,
            });
          }}
          onRemoveMembers={onRemoveMembers}
          onMemberAvatarClick={onMemberAvatarClick}
          closeAllClick={() => setOpenInfoDrawer(false)}
        />
        {isGroupManager && (
          <>
            <GroupManagement
              sessionStore={sessionStore}
              isGroupOwner={isGroupOwner}
              onMemberAvatarClick={onMemberAvatarClick}
              onTransferGroupOwnerClick={onTransferGroupOwnerClick}
              closeAllClick={() => setOpenInfoDrawer(false)}
            />
            <GroupItem
              icon={<JoinRequestIcon />}
              title={t('action.joinRequests')}
              onClick={handleJoinRequestsClick}
            />
          </>
        )}
        <MuteNotificationsCardPlus session={session} />
        <PinnedCard session={session} />

        <SaveToContactsCard
          open={!!saveToContactsCategory}
          loading={loadingSaveToContactsCategory}
          onOpen={() => {
            runSaveToContactsCategory(group?.gid, 'add');
          }}
          onClose={() => {
            runSaveToContactsCategory(group?.gid, 'remove');
          }}
        />
        <AutoDeleteMessagesCard value={session?.expire_duration} onSelect={onDurationChange} />
        <ClearAllChatHistory onClearAllChatHistory={onClearAllChatHistory_} />
        <ReportCard handleReport={() => setReportIsOpen(true)} />

        <FooterButtons
          members={members}
          setOpenInfoDrawer={setOpenInfoDrawer}
          groupInfo={groupInfo}
          isGroupOwner={isGroupOwner}
          isGroupManager={isGroupManager}
          onTransferGroupOwnerClick={onTransferGroupOwnerClick}
        />
      </Stack>
    </ChatPanelDrawer>
  );
});
